import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import type { UserOptionFragment } from '__generated__/graphql';
import { getUsersName } from './formatting';

export function getMultiDropdownSelectOptions<T extends { id: number }, K extends keyof T>(
  data: T[],
  nameField: K,
  selectedIds?: number[] | null,
  selectedData?: Pick<T, 'id' | K>[] | null
): DropdownOption[] {
  const getDropdownOption = (entry: Pick<T, 'id' | K>) => ({
    name: entry[nameField] == null ? '' : String(entry[nameField]),
    id: entry.id,
  });

  if (selectedData) {
    return selectedData.map((t) => getDropdownOption(t));
  } else if (data && selectedIds) {
    const idSet = new Set(selectedIds);
    const res = data.filter((x) => idSet.has(x.id));
    return res.map((x) => getDropdownOption(x));
  } else {
    return [];
  }
}

export function getSingleDropdownSelectOptions<T extends { id: number }, K extends keyof T>(
  data: T[],
  nameField: K,
  selectedId?: number | null,
  selectedData?: Pick<T, 'id' | K> | null
): DropdownOption | undefined {
  if (selectedData) {
    return {
      id: selectedData.id,
      name: selectedData[nameField] == null ? '' : String(selectedData[nameField]),
    };
  } else if (data && selectedId != null) {
    const item = data.find((t) => t.id === selectedId);
    return (
      item && { id: item?.id ?? 0, name: item[nameField] == null ? '' : String(item[nameField]) }
    );
  } else {
    return;
  }
}

export function getMultiDropdownSelectOptionsForUsers(
  data: Pick<UserOptionFragment, 'id' | 'firstName' | 'lastName'>[],
  selectedUserIds?: number[] | null,
  selectedUsers?: Pick<UserOptionFragment, 'id' | 'firstName' | 'lastName'>[] | null,
  previousOptions: DropdownOption[] = []
): DropdownOption[] {
  if (selectedUsers != null) {
    const selected = selectedUsers.map((user) => ({ id: user.id, name: getUsersName(user) }));
    // An explicitly empty selection still clears the control. Partial fetched users must
    // retain IDs whose related User is nullable, so the next edit cannot drop recipients.
    if (!selected.length) return [];
    const knownIds = new Set(selected.map(({ id }) => id));
    return [
      ...selected,
      ...getMultiDropdownSelectOptionsForUsers(
        data,
        selectedUserIds?.filter((id) => !knownIds.has(id)),
        null,
        previousOptions
      ),
    ];
  }
  return (selectedUserIds ?? []).map((id) => {
    const user = data.find((entry) => entry.id === id);
    return user
      ? { id, name: getUsersName(user) }
      : (previousOptions.find((option) => option.id === id) ?? { id, name: `User ${id}` });
  });
}

// Autocomplete needs selected records in its option set even when a filtered query omits them.
export function includeSelectedOptions(options: DropdownOption[], selected: DropdownOption[]) {
  const ids = new Set(options.map(({ id }) => String(id)));
  return [...options, ...selected.filter(({ id }) => !ids.has(String(id)))];
}
